import { useState } from "react";
import { AlertTriangle, Check } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";

/** Referrer-only notice when a verified business has a paused reward. */
export function PausedRewardNotice({ branch, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(!!branch.reward_reminder_sent);
  const n = branch.my_referral_count ?? 0;
  const name = branch.company_name;

  const remind = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/branches/${branch.id}/reward-reminder`);
      setSent(true);
      onUpdated?.({ ...branch, reward_reminder_sent: true });
      if (data.already_sent) {
        toast.message("Reminder already sent for this pause.");
      } else {
        toast.success("Reminder sent");
      }
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-[14px] border border-brand-gold/40 bg-brand-gold/10 p-5"
      data-testid="paused-reward-notice"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-brand-gold-dark" />
        <div className="flex-1">
          <p className="font-bold text-foreground">Rewards are paused</p>
          <p className="mt-1 text-sm text-muted-foreground">
            New referrals you send right now won&apos;t earn a reward. Your {n} past referral
            {n === 1 ? "" : "s"} aren&apos;t affected.
          </p>
          {sent ? (
            <p
              className="mt-3 flex items-center gap-2 text-sm font-medium text-brand-green"
              data-testid="reward-reminder-sent"
            >
              <Check className="h-4 w-4" />
              Reminder sent. We&apos;ll notify you if the reward comes back.
            </p>
          ) : (
            <Button
              className="mt-3 bg-brand-green hover:bg-brand-green-dark"
              disabled={busy}
              onClick={remind}
              data-testid="remind-business-btn"
            >
              {busy ? "Sending…" : `Remind ${name}`}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
